import StarIcon from '@/components/icons/StarIcon';
import StarFilledIcon from '@/components/icons/StarFilledIcon';

export type ReviewCardTestimonial = {
  name: string;
  time: string;
  rating: number;
  review: string;
  avatar?: string;
};

type Props = {
  testimonial: ReviewCardTestimonial;
  widthClass?: string;
  className?: string;
  /** Browse grid: equal-height cells, tighter padding, review body fills and truncates with ellipsis. */
  fillGridCell?: boolean;
};

export default function ReviewTestimonialCard({
  testimonial,
  widthClass = 'w-full',
  className = '',
  fillGridCell = false,
}: Props) {
  const rootShrink = fillGridCell ? '' : 'flex-shrink-0';
  const rootLayout = fillGridCell
    ? 'flex h-full min-h-[196px] flex-col sm:min-h-[208px]'
    : '';

  const reviewText = (
    <p
      title={testimonial.review}
      className={
        fillGridCell
          ? 'text-body-14-mobile font-medium leading-relaxed text-text-secondary line-clamp-3 break-words sm:text-body-16-desktop sm:line-clamp-4'
          : 'max-h-[98px] overflow-hidden text-body-14-mobile font-medium leading-relaxed text-text-secondary line-clamp-4 break-words sm:text-body-16-desktop sm:line-clamp-5'
      }
    >
      {testimonial.review}
    </p>
  );

  return (
    <div
      className={`${rootShrink} ${rootLayout} ${widthClass} bg-constant-white rounded-2xl border border-neutral-10 ${
        fillGridCell ? 'p-3 sm:p-4' : 'p-4 sm:p-6'
      } ${className}`.trim()}
    >
      <div
        className={`flex flex-col ${fillGridCell ? 'gap-1.5 sm:gap-2' : 'gap-2 sm:gap-3'} ${
          fillGridCell ? 'min-h-0 flex-1' : ''
        }`.trim()}
      >
        <div className={`flex items-start ${fillGridCell ? 'gap-2' : 'gap-3'}`}>
          <div
            className={`shrink-0 overflow-hidden border border-neutral-10 bg-neutral-5 ${
              fillGridCell
                ? 'h-9 w-9 rounded-lg sm:h-11 sm:w-11'
                : 'h-10 w-10 rounded-xl sm:h-14 sm:w-14'
            }`}
          >
            {testimonial.avatar ? (
              <img
                src={testimonial.avatar}
                alt={testimonial.name}
                width={fillGridCell ? 44 : 56}
                height={fillGridCell ? 44 : 56}
                className="h-full w-full object-cover"
                onError={(e) => {
                  e.currentTarget.src = `https://i.pravatar.cc/150?img=${Math.floor(Math.random() * 70) + 1}`;
                }}
              />
            ) : (
              <div
                className={`flex h-full w-full items-center justify-center bg-blue-100 font-semibold text-constant-white ${
                  fillGridCell ? 'text-sm' : 'text-base'
                }`}
              >
                {testimonial.name.charAt(0).toUpperCase()}
              </div>
            )}
          </div>

          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <div className="flex items-center justify-between gap-2">
              <span className="truncate text-sm font-semibold text-text-primary sm:text-base">
                {testimonial.name}
              </span>
              <div className="flex shrink-0 items-center gap-[3px]">
                {[...Array(5)].map((_, i) =>
                  i < testimonial.rating ? (
                    <StarFilledIcon key={i} size={14} color="#343B91" className="h-3.5 w-3.5 sm:h-5 sm:w-5" />
                  ) : (
                    <StarIcon key={i} size={14} color="#E3E4F0" className="h-3.5 w-3.5 sm:h-5 sm:w-5" />
                  )
                )}
              </div>
            </div>
            <span className="text-xs font-medium text-text-secondary sm:text-sm">{testimonial.time}</span>
          </div>
        </div>

        <div className="h-px w-full shrink-0 bg-neutral-10" />

        {fillGridCell ? <div className="min-h-0 min-w-0 flex-1">{reviewText}</div> : reviewText}
      </div>
    </div>
  );
}
